// ---------------------------------------------------------------------------
// The Offering step's rules (R17b)
//
// Which of its two sections opens, and the talking points. Taken out of
// wizard/wiz-offering.ts unchanged, so the step's rebuilds (R17i–k) redo only
// the markup. Plain TypeScript. The AI analysis (what an applied result
// writes, and the source documents it records) stays in the step for R17k:
// its flow needs the document pipeline, which no test here drives.
// ---------------------------------------------------------------------------

import type { OfferingConfig } from '../types.js';

/** Whether a manual offering is already filled in (the manual section's "Filled" dot). */
export const hasManualContent = (offering: OfferingConfig): boolean =>
  !!offering.name.trim() || !!offering.description.trim() || offering.talkingPoints.length > 0;

/**
 * The two sections when the step is first shown: with a manual offering filled
 * in, the manual section opens; otherwise the AI-powered one does. The
 * AI-powered section also stays open when uploads, websites or parsed
 * documents were restored, so the fetched content is visible again.
 */
export function openSections(
  offering: OfferingConfig,
  restored: { parsedDocs: number; websites: number; files: number; cloudFiles: number },
): { aiOpen: boolean; manualOpen: boolean } {
  const manual = hasManualContent(offering);
  const hasRestoredUploads =
    restored.parsedDocs > 0 || restored.websites > 0 || restored.files > 0 || restored.cloudFiles > 0;
  return { aiOpen: !manual || hasRestoredUploads, manualOpen: manual };
}

/** The offering with one more talking point, or null when the text is empty. */
export const withTalkingPoint = (offering: OfferingConfig, text: string): OfferingConfig | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  return { ...offering, talkingPoints: [...offering.talkingPoints, trimmed] };
};

/** The offering without the talking point at `index`. */
export const withoutTalkingPoint = (offering: OfferingConfig, index: number): OfferingConfig => ({
  ...offering,
  talkingPoints: offering.talkingPoints.filter((_, i) => i !== index),
});
